/**
 * 性能原型页（设计文档 M0：24 梯 × 60 层 Canvas 渲染压测，NF-001）。
 *
 * 合成状态以 ~2 层/秒在 0..59 层往返，每帧自绘；测量预热后 3 秒平均帧率，
 * 写入 #perf-fps / #perf-status 供 Playwright 自动校验（硬门 ≥ 30 FPS）。
 */
import { useEffect, useRef } from 'react';
import { renderShafts, type RenderCar } from '../render/shaft-renderer';

const SHAFTS = 24;
const FLOORS = 60;

export function PerfBenchmark() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fpsEl = useRef<HTMLSpanElement>(null);
  const statusEl = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width = Math.floor(r.width * dpr);
      canvas.height = Math.floor(r.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // 合成运动状态：24 部梯分布于全楼层
    const cars: RenderCar[] = Array.from({ length: SHAFTS }, (_, i) => ({
      id: `c${i}`,
      floor: (i * 2.5) % FLOORS,
      direction: i % 2 ? -1 : 1,
      load: (i * 5) % 14,
      capacity: 13,
      doorOpen: false,
    }));

    let raf = 0;
    let last = performance.now();
    let warmupEnd = last + 500;
    let measureStart = last;
    let frames = 0;
    let done = false;

    const loop = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      for (const c of cars) {
        c.floor += c.direction * 2 * dt;
        if (c.floor >= FLOORS - 1) {
          c.floor = FLOORS - 1;
          c.direction = -1;
          c.doorOpen = true;
        } else if (c.floor <= 0) {
          c.floor = 0;
          c.direction = 1;
          c.doorOpen = true;
        } else {
          c.doorOpen = false;
        }
      }
      const r = canvas.getBoundingClientRect();
      renderShafts(ctx, { cars }, { width: r.width, height: r.height, floors: FLOORS });

      if (now < warmupEnd) {
        measureStart = now;
        frames = 0;
      } else {
        frames++;
      }
      if (!done && now - measureStart >= 3000 && frames > 0) {
        const fps = frames / ((now - measureStart) / 1000);
        if (fpsEl.current) fpsEl.current.textContent = fps.toFixed(1);
        if (statusEl.current) statusEl.current.textContent = `（${fps >= 30 ? '达标' : '未达标'}）`;
        done = true;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [fpsEl, statusEl]);

  return (
    <div style={{ padding: 16, fontFamily: 'system-ui, sans-serif', color: '#e5e7eb', background: '#0f1115', minHeight: '100vh' }}>
      <h2 style={{ marginTop: 0 }}>
        性能原型 — {SHAFTS} 梯 × {FLOORS} 层 Canvas 渲染压测
      </h2>
      <p>合成状态每帧自绘井道与轿厢，测量预热后 3 秒平均帧率（硬门 ≥ 30 FPS，NF-001）。</p>
      <div style={{ marginBottom: 8 }}>
        FPS: <b><span id="perf-fps" ref={fpsEl}>测量中…</span></b>{' '}
        <span id="perf-status" ref={statusEl} />
      </div>
      <canvas ref={canvasRef} style={{ width: '100%', height: '68vh', background: '#0f1115' }} />
    </div>
  );
}
